/**
 * Additive Inverse generator (6.PAFR.3.3).
 *
 * The additive inverse (opposite) of a number is what you add to it to get 0.
 * The value is chosen first and the answer is derived with Rational.neg(), so
 * the graded answer is exact and matches the prompt.
 *
 * Families:
 *   F1: "What is the additive inverse of n?"   (answer = -n)
 *   F2: "What value makes n + ___ = 0?"        (answer = -n)
 */

import { rat, type Rational } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "additive-inverse" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2";

interface Cfg {
  readonly families: readonly Family[];
  readonly intMax: number;
  /** Probability the value is negative. */
  readonly pNeg: number;
  /** Whether fraction values are allowed. */
  readonly allowFraction: boolean;
  readonly denomMax: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1", "F2"], intMax: 12, pNeg: 0.5, allowFraction: false, denomMax: 4 },
  standard: { families: ["F1", "F2"], intMax: 25, pNeg: 0.5, allowFraction: false, denomMax: 6 },
  hard: { families: ["F1", "F2"], intMax: 40, pNeg: 0.55, allowFraction: true, denomMax: 9 },
  challenge: { families: ["F1", "F2"], intMax: 60, pNeg: 0.6, allowFraction: true, denomMax: 12 },
};

export class AdditiveInverseGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);

      const sign = rng.sign(cfg.pNeg);
      let value: Rational;
      if (cfg.allowFraction && rng.bool(0.4)) {
        const den = rng.int(2, cfg.denomMax);
        const num = rng.int(1, den - 1);
        value = rat(num * sign, den);
      } else {
        value = rat(rng.int(1, cfg.intMax) * sign);
      }

      const answer = value.neg();

      let promptText: string;
      let promptLatex: string;
      let steps: { description: string; latex: string }[];

      if (family === "F1") {
        promptText = `What is the additive inverse of ${value.toString()}?`;
        promptLatex = `\\text{Find the additive inverse of } ${value.toLatex()}`;
        steps = [
          {
            description: "The additive inverse is the opposite: change its sign.",
            latex: `${value.toLatex()} \\rightarrow ${answer.toLatex()}`,
          },
          {
            description: "Check: a number plus its opposite equals 0.",
            latex: `${value.toLatex()} + ${answer.toLatex()} = 0`,
          },
        ];
      } else {
        promptText = `What value makes ${value.toString()} + ___ = 0?`;
        promptLatex = `${value.toLatex()} + \\square = 0`;
        steps = [
          {
            description: "The blank must be the opposite of the given number.",
            latex: `\\square = ${answer.toLatex()}`,
          },
          {
            description: "Check the sum is 0.",
            latex: `${value.toLatex()} + ${answer.toLatex()} = 0`,
          },
        ];
      }

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: { text: promptText, latex: promptLatex },
        answerType: "number",
        answer: { kind: "number", value: answer },
        steps,
        hints: [
          { level: 1, text: "The additive inverse of a number is its opposite." },
          { level: 2, text: "A number plus its additive inverse always equals 0." },
        ],
        subskills: topic.subskills,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "number" ? q.answer.value.key() : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const additiveInverse = new AdditiveInverseGenerator();
